import React, { useEffect, useCallback } from "react";
import "./ImagingArmMovementControl.css";
import { EMoveArmDirection } from "../../Model/EMoveImagingArmButton";

type Props = {
  style?: React.CSSProperties;
  onMouseDown: (direction: EMoveArmDirection) => void;
  onMouseUp: () => void;
  noCenter?: boolean;
};

type ButtonProps = {
  style?: React.CSSProperties;
  onMouseDown: (direction: EMoveArmDirection) => void;
  onMouseUp: () => void;
  direction: EMoveArmDirection;
};

function ImagingArmMovementButton({ style = {}, onMouseDown, onMouseUp, direction }: ButtonProps) {
  let angle = 0;
  let className = "imaging-arm-movement-button";
  switch (direction) {
    case EMoveArmDirection.West: {
      angle = 0;
      break;
    }
    case EMoveArmDirection.East: {
      angle = 180;
      break;
    }
    case EMoveArmDirection.Up:
    case EMoveArmDirection.North: {
      angle = 90;
      break;
    }
    case EMoveArmDirection.Down:
    case EMoveArmDirection.South: {
      angle = -90;
      break;
    }
    case EMoveArmDirection.Center: {
      className = "imaging-arm-movement-center";
      break;
    }
  }

  return (
    <button
      style={{ ...style }}
      className={className}
      onMouseDown={() => onMouseDown(direction)}
      onMouseUp={() => onMouseUp()}
    >
      <div
        style={{
          margin: "auto",
        }}
      >
        {direction !== EMoveArmDirection.Center && (
          <svg
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            transform={`rotate(${angle})`}
          >
            <path d="M10 0L0 8L10 16V11V10V6V5L10 0Z" fill="white" />
          </svg>
        )}
        {direction === EMoveArmDirection.Center && "C"}
      </div>
    </button>
  );
}

function getDirection(event: KeyboardEvent) {
  if (event.repeat) {
    return EMoveArmDirection.Unknown;
  }
  const element = event.target as Element;
  if (element && element.matches("input")) {
    return EMoveArmDirection.Unknown;
  }
  if (event.location !== event.DOM_KEY_LOCATION_NUMPAD) {
    return EMoveArmDirection.Unknown;
  }

  let direction = EMoveArmDirection.Unknown;

  switch (event.key) {
    case "4":
    case "ArrowLeft": {
      direction = EMoveArmDirection.West;
      break;
    }
    case "6":
    case "ArrowRight": {
      direction = EMoveArmDirection.East;
      break;
    }

    case "8":
    case "ArrowUp": {
      direction = EMoveArmDirection.North;
      break;
    }
    case "2":
    case "ArrowDown": {
      direction = EMoveArmDirection.South;
      break;
    }

    case "9":
    case "PageUp": {
      direction = EMoveArmDirection.Up;
      break;
    }
    case "3":
    case "PageDown": {
      direction = EMoveArmDirection.Down;
      break;
    }

    case "5":
    case "Clear": {
      if (event.shiftKey) {
        direction = EMoveArmDirection.Center;
      }
      break;
    }
  }
  return direction;
}

export default function ImagingArmMovementControlV2({ style = {}, onMouseDown, onMouseUp, noCenter = false }: Props) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const direction = getDirection(event);

      if (direction !== EMoveArmDirection.Unknown) {
        onMouseDown(direction);
      }
    },
    [onMouseDown]
  );

  const onKeyUp = useCallback((event: KeyboardEvent) => {
    const direction = getDirection(event);

    if (direction !== EMoveArmDirection.Unknown) {
      onMouseUp();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown, false);
    document.addEventListener("keyup", onKeyUp, false);

    return () => {
      document.removeEventListener("keydown", onKeyDown, false);
      document.removeEventListener("keyup", onKeyUp, false);
    };
  }, []);
  return (
    <div className="imaging-arm-movement" style={{ ...style }}>
      <span
        style={{
          gridArea: "label-x",
        }}
      >
        X
      </span>
      <span
        style={{
          gridArea: "label-y",
        }}
      >
        Y
      </span>
      <span
        style={{
          gridArea: "label-z",
        }}
      >
        Z
      </span>

      <ImagingArmMovementButton
        style={{
          gridArea: "button-xn",
        }}
        direction={EMoveArmDirection.West}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />

      <ImagingArmMovementButton
        style={{
          gridArea: "button-xp",
        }}
        direction={EMoveArmDirection.East}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />

      <ImagingArmMovementButton
        style={{
          gridArea: "button-yn",
        }}
        direction={EMoveArmDirection.South}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />
      <ImagingArmMovementButton
        style={{
          gridArea: "button-yp",
        }}
        direction={EMoveArmDirection.North}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />

      <ImagingArmMovementButton
        style={{
          gridArea: "button-zp",
        }}
        direction={EMoveArmDirection.Up}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />

      <ImagingArmMovementButton
        style={{
          gridArea: "button-zn",
        }}
        direction={EMoveArmDirection.Down}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />

      <ImagingArmMovementButton
        style={{
          gridArea: "button-center",
        }}
        direction={EMoveArmDirection.Center}
        onMouseDown={onMouseDown}
        onMouseUp={() => {}}
      />
    </div>
  );
}
